/**
 * Profile Page - Redesigned
 * Elegant, minimalist design with smooth animations
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppConfig } from '@/contexts/AppConfigContext.jsx';
import { billingAPI, userAPI } from '@/services/api';
import { trackOnce } from '@/services/analytics';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import { GracePeriodBanner } from '@/components/ui/GracePeriodBanner';
import { getInitials, stringToColor } from '@/lib/utils';
import toast from 'react-hot-toast';

// Icons
const IconUser = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const IconShield = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const IconCreditCard = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const IconDownload = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const IconTrash = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconSun = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const IconMoon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const IconCheck = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const IconArrowLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const IconSparkles = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const ProfilePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, toggleTheme, isDark } = useTheme();
  const { 
    currentUser, 
    userProfile, 
    updateUserProfile, 
    changePassword, 
    logout,
    sendVerificationEmail,
    refreshUser,
    loading: authLoading,
    isPremium,
    isTrialing,
    isAdmin,
    isGracePeriod,
    refreshPremiumClaim
  } = useAuth();
  const { premiumCopy } = useAppConfig();

  // State
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [updating, setUpdating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteFeedback, setDeleteFeedback] = useState('');
  const [acknowledgeDelete, setAcknowledgeDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Effects
  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      toast.success('🎉 Payment successful! Welcome to Premium!');
      refreshPremiumClaim?.();
      setSearchParams({});
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refreshPremiumClaim]);

  useEffect(() => {
    if (currentUser && userProfile) {
      setProfileData({
        displayName: userProfile.displayName || currentUser.displayName || '',
        email: currentUser.email || '',
      });
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!currentUser) return;
      try {
        const info = await billingAPI.getStatus();
        if (active) setTrialInfo(info);
      } catch (_) {}
    })();
    return () => { active = false; };
  }, [currentUser]);

  // Handlers
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!profileData.displayName.trim()) {
      setErrors({ displayName: 'Display name is required' });
      return;
    }
    setUpdating(true);
    try {
      await updateUserProfile({ displayName: profileData.displayName.trim() });
      toast.success('Profile updated');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!passwordData.currentPassword) newErrors.currentPassword = 'Required';
    if (!passwordData.newPassword) newErrors.newPassword = 'Required';
    else if (passwordData.newPassword.length < 6) newErrors.newPassword = 'Min 6 characters';
    if (passwordData.newPassword !== passwordData.confirmPassword) newErrors.confirmPassword = 'Passwords don\'t match';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed');
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const payload = await userAPI.exportData();
      const filename = `finlist-export-${Date.now()}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE' || !acknowledgeDelete) {
      setDeleteError('Please complete all requirements');
      return;
    }
    setDeletingAccount(true);
    try {
      await userAPI.deleteAccount({ confirmPhrase: deleteConfirm, feedback: deleteFeedback });
      toast.success('Account deleted');
      await logout();
      // RULE 1: After account deletion, redirect to /welcome
      window.location.href = '/welcome';
    } catch (error) {
      setDeleteError(error?.message || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  // Computed
  const trialMeta = (() => {
    if (!trialInfo) return { daysRemaining: 30, expired: false };
    const rawEnd = Number(trialInfo.trialEnd) || 0;
    const effectiveEnd = Number(trialInfo.effectiveTrialEnd || rawEnd);
    const nowTs = trialInfo.now || Date.now();
    const remainMs = effectiveEnd - nowTs;
    if (remainMs > 0) {
      return { daysRemaining: Math.ceil(remainMs / 86400000), expired: false };
    }
    return { daysRemaining: 0, expired: true };
  })();

  const membershipStatus = isAdmin ? 'admin' : isPremium ? 'premium' : isTrialing ? 'trial' : 'free';

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: IconUser },
    { id: 'security', label: 'Security', icon: IconShield },
    { id: 'billing', label: 'Billing', icon: IconCreditCard },
    { id: 'data', label: 'Data', icon: IconDownload },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors duration-500">
      {/* Animated background gradient */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 dark:from-teal-500/10 dark:to-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-violet-400/20 to-purple-400/20 dark:from-violet-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors group"
          >
            <IconArrowLeft />
            <span className="text-sm font-medium group-hover:underline">Dashboard</span>
          </Link>
          
          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105"
            aria-label="Toggle theme"
          >
            {isDark ? <IconSun /> : <IconMoon />}
          </button>
        </header>

        {/* Profile Hero */}
        <section className="relative mb-8 p-6 sm:p-8 rounded-3xl bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200/50 dark:border-slate-700/50 shadow-xl shadow-gray-200/50 dark:shadow-black/20 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-teal-500/10 to-transparent rounded-bl-full" />
          
          <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="relative group">
              <div
                className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-lg transition-transform duration-300 group-hover:scale-105"
                style={{ 
                  backgroundColor: stringToColor(profileData.displayName || profileData.email),
                  boxShadow: `0 20px 40px -12px ${stringToColor(profileData.displayName || profileData.email)}80`
                }}
              >
                {currentUser?.photoURL ? (
                  <img src={currentUser.photoURL} alt="" className="w-full h-full rounded-2xl object-cover" />
                ) : (
                  getInitials(profileData.displayName || profileData.email)
                )}
              </div>
              {/* Status badge */}
              <div className={`absolute -bottom-2 -right-2 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide shadow-lg ${
                isGracePeriod ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white' :
                membershipStatus === 'admin' ? 'bg-purple-500 text-white' :
                membershipStatus === 'premium' ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white' :
                membershipStatus === 'trial' ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' :
                'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
              }`}>
                {isGracePeriod ? '🎉 Free' : membershipStatus}
              </div>
            </div>

            {/* User info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
                {profileData.displayName || 'Your Profile'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{profileData.email}</p>
              
              {/* Quick stats */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-4">
                {isGracePeriod ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                    <IconSparkles />
                    <span>Free until Mar 31, 2026</span>
                  </div>
                ) : membershipStatus === 'trial' && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-sm font-medium">
                    <IconSparkles />
                    <span>{trialMeta.daysRemaining} days left</span>
                  </div>
                )}
                {!currentUser?.emailVerified && (
                  <button
                    onClick={sendVerificationEmail}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-sm font-medium hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                  >
                    ⚠️ Verify email
                  </button>
                )}
              </div>
            </div>

            {/* Sign out button */}
            <button
              onClick={logout}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
            >
              Sign out
            </button>
          </div>
        </section>

        {/* Tab Navigation */}
        <nav className="flex gap-1 p-1.5 mb-6 rounded-2xl bg-gray-100/80 dark:bg-slate-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-slate-700/50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
              }`}
            >
              <tab.icon />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className="animate-fadeIn">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <section className="p-6 sm:p-8 rounded-3xl bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200/50 dark:border-slate-700/50 shadow-xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Profile Settings</h2>
              
              <form onSubmit={handleUpdateProfile} className="space-y-6 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={profileData.displayName}
                    onChange={(e) => setProfileData(p => ({ ...p, displayName: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 transition-all"
                    disabled={updating}
                  />
                  {errors.displayName && <p className="mt-1 text-sm text-red-500">{errors.displayName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-slate-900/80 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                </div>

                <button
                  type="submit"
                  disabled={updating}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 disabled:opacity-50 transition-all duration-300 hover:-translate-y-0.5"
                >
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </section>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <section className="p-6 sm:p-8 rounded-3xl bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200/50 dark:border-slate-700/50 shadow-xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Security</h2>
              
              {currentUser?.providerData?.[0]?.providerId === 'google.com' ? (
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700">
                  <p className="text-gray-600 dark:text-gray-400">
                    You signed in with Google. Password is managed through your Google account.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-6 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(p => ({ ...p, newPassword: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="w-full py-3 px-4 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 transition-all duration-300 hover:-translate-y-0.5"
                  >
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              )}
            </section>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <section className="p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-500 text-white shadow-2xl shadow-teal-500/25 overflow-hidden relative">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Plan & Billing</h2>
                  <span className="px-3 py-1 rounded-full bg-white/20 text-sm font-medium uppercase">
                    {isGracePeriod ? 'Launch Offer' : membershipStatus}
                  </span>
                </div>

                {/* Grace Period Banner - shows during launch offer */}
                {isGracePeriod && (
                  <div className="mb-6 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl">
                        🎉
                      </div>
                      <div>
                        <p className="font-medium">Launch Offer: All features free until March 31, 2026!</p>
                        <p className="text-sm text-white/70">No payment required during the launch period</p>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-white/90 mb-6 max-w-lg">
                  {isGracePeriod
                    ? 'Enjoy full access to all premium features during our launch period. After March 31, 2026, you can continue with a subscription or use Shopping List for free.'
                    : isPremium 
                      ? 'You have full access to all premium features. Manage your subscription anytime.'
                      : `Enjoy complete access to all Pro financial tools. Continue after trial for only ${premiumCopy?.pricePerUserPerMonth || '£2.99/month'}.`
                  }
                </p>

                {!isPremium && !isGracePeriod && trialMeta.daysRemaining > 0 && (
                  <div className="mb-6 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl font-bold">
                        {trialMeta.daysRemaining}
                      </div>
                      <div>
                        <p className="font-medium">Days remaining in trial</p>
                        <p className="text-sm text-white/70">All features unlocked</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {isPremium ? (
                    <button
                      onClick={async () => {
                        try {
                          const { url } = await billingAPI.createPortalSession();
                          if (url) window.location.assign(url);
                        } catch (e) {
                          toast.error('Failed to open billing portal');
                        }
                      }}
                      className="px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium backdrop-blur-sm border border-white/30 transition-all duration-300 hover:-translate-y-0.5"
                    >
                      Manage Subscription
                    </button>
                  ) : isGracePeriod ? (
                    // During grace period - no payment prompts needed
                    <div className="px-6 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-center">
                      <p className="font-medium">✨ Enjoying all features for free!</p>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const { url } = await billingAPI.createCheckoutSession();
                          if (url) window.location.assign(url);
                        } catch (e) {
                          toast.error('Failed to start checkout');
                        }
                      }}
                      className="px-6 py-3 rounded-xl bg-white text-teal-600 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                    >
                      Upgrade to Premium
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Data Tab */}
          {activeTab === 'data' && (
            <section className="p-6 sm:p-8 rounded-3xl bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200/50 dark:border-slate-700/50 shadow-xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Your Data</h2>
              
              <div className="space-y-6">
                {/* Export */}
                <div className="p-5 rounded-2xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                      <IconDownload />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">Export Data</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Download a copy of all your data in JSON format.
                      </p>
                      <button
                        onClick={handleExportData}
                        disabled={exporting}
                        className="px-4 py-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                      >
                        {exporting ? 'Preparing...' : 'Export My Data'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Delete Account */}
                <div className="p-5 rounded-2xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                      <IconTrash />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-red-700 dark:text-red-400 mb-1">Delete Account</h3>
                      <p className="text-sm text-red-600/80 dark:text-red-400/70 mb-3">
                        Permanently delete your account and all associated data. This cannot be undone.
                      </p>
                      <button
                        onClick={() => setDeleteModalOpen(true)}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-all"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        title="Delete Account"
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        confirmText={deletingAccount ? 'Deleting...' : 'Delete Account'}
        confirmDisabled={deleteConfirm !== 'DELETE' || !acknowledgeDelete}
        confirmLoading={deletingAccount}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            This will permanently delete all your data. This action cannot be undone.
          </p>
          
          <div>
            <label className="block text-sm font-medium mb-1">Why are you leaving? (optional)</label>
            <textarea
              rows={2}
              value={deleteFeedback}
              onChange={(e) => setDeleteFeedback(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Help us improve..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Type DELETE to confirm</label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={acknowledgeDelete}
              onChange={(e) => setAcknowledgeDelete(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              I understand this action cannot be undone.
            </span>
          </label>
          
          {deleteError && <p className="text-sm text-red-500">{deleteError}</p>}
        </div>
      </Modal>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProfilePage;
